import { getGASummary, getTopPages, getTrafficByDevice, getUsersByCountry } from '@src/api/analytics';
import { useQuery } from '@tanstack/react-query';

const parseRows = (res: any, dimensionKeys: string[], metricKeys: string[]) => {
  if (!res?.rows) return [];
  return res.rows.map((row: any) => {
    const item: Record<string, string> = {};
    dimensionKeys.forEach((k, i) => { item[k] = row.dimensionValues?.[i]?.value ?? ''; });
    metricKeys.forEach((k, i) => { item[k] = row.metricValues?.[i]?.value ?? '0'; });
    return item;
  });
};

const parseSummary = (res: any) => {
  const values = res?.rows?.[0]?.metricValues ?? [];
  return {
    activeUsers: values[0]?.value ?? '0',
    sessions: values[1]?.value ?? '0',
    pageViews: values[2]?.value ?? '0',
    bounceRate: values[3]?.value ?? '0',
    avgSessionDuration: values[4]?.value ?? '0',
  };
};

export const useAnalyticsService = (
  token: string | null,
  dateRange: { startDate: string; endDate: string; label?: string },
  propertyId: string,
) => {
  const { startDate, endDate } = dateRange;
  dateRange = { startDate, endDate };
  const enabled = !!token && !!propertyId;
  const opts = { staleTime: 5 * 60 * 1000, enabled };

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['ga-summary', dateRange, propertyId],
    queryFn: () => getGASummary(token!, propertyId, dateRange),
    select: parseSummary,
    ...opts,
  });

  const { data: topPages = [], isLoading: pagesLoading } = useQuery({
    queryKey: ['ga-top-pages', dateRange, propertyId],
    queryFn: () => getTopPages(token!, propertyId, dateRange),
    select: (res) => parseRows(res, ['pagePath', 'pageTitle'], ['pageViews', 'sessions', 'activeUsers']),
    ...opts,
  });

  const { data: byCountry = [], isLoading: countryLoading } = useQuery({
    queryKey: ['ga-by-country', dateRange, propertyId],
    queryFn: () => getUsersByCountry(token!, propertyId, dateRange),
    select: (res) => parseRows(res, ['country'], ['activeUsers', 'sessions', 'newUsers']),
    ...opts,
  });

  const { data: byDevice = [], isLoading: deviceLoading } = useQuery({
    queryKey: ['ga-by-device', dateRange, propertyId],
    queryFn: () => getTrafficByDevice(token!, propertyId, dateRange),
    select: (res) => parseRows(res, ['deviceCategory'], ['sessions', 'activeUsers', 'pageViews']),
    ...opts,
  });

  return { summary, summaryLoading, topPages, pagesLoading, byCountry, countryLoading, byDevice, deviceLoading };
};
